// Client-side upload preflight for the POOLED account storage allowance.
//
// There is exactly ONE ceiling: the tier's pooled storage entitlement
// (`storageGb`), shared across an unlimited number of projects (#342). The
// per-project photo/byte caps that used to live here are gone — server-side
// too (packages/functions/shared/retention.py) — so this file must not grow a
// second ceiling: two ceilings is how the client and server drift apart.
//
// This check is a UX guardrail that gives a good error before a big drop starts
// uploading. The SERVER is the truth: the ingest trigger meters the same pooled
// allowance against a maintained account aggregate and evicts an arriving photo
// that doesn't fit.

// Days of project inactivity after which the sweep reclaims raw photos on the
// Free tier. Mirrors RETENTION_DAYS / WARNING_LEAD_DAYS in
// packages/functions/shared/retention.py — keep the two in sync.
export const RETENTION_DAYS = 30;
export const RETENTION_WARNING_LEAD_DAYS = 7;

// Bytes per "GB" of a tier's pooled storage allowance. Uses GiB (1024^3) so it
// renders consistently with formatBytes() (which also divides by 1024^3), i.e.
// a 2 GB Free pool shows as "2.00 GB".
//
// Mirrored as STORAGE_GB_BYTES in packages/functions/shared/entitlements.py — a
// JS module and a Python module can't share a literal, and if the two disagree
// the dialog says "fits" for a batch the ingest trigger then evicts. Pinned by
// packages/functions/tests/test_photo_cap_events.py.
export const POOL_GB_BYTES = 1024 * 1024 * 1024;

// Convert a tier's storageGb entitlement (e.g. Free 2, Solo 50) to bytes.
// Returns null when there is no numeric allowance so callers can treat the
// pooled meter as "unknown" rather than a 0-byte pool.
export function poolBytesFromGb(storageGb) {
  return Number.isFinite(storageGb) && storageGb > 0 ? storageGb * POOL_GB_BYTES : null;
}

// Preflight estimate uses a static 20 Mbps up assumption; live ETA replaces
// this once real throughput readings come in from the queue.
export const ASSUMED_UPLOAD_BPS = (20 * 1000 * 1000) / 8; // ~2.5 MB/s

export function evaluateCaps({
  // Project context, reported for display/analytics only — nothing here gates
  // an upload any more (#342 removed the per-project caps).
  existingPhotos = 0,
  existingBytes = 0,
  batchPhotos = 0,
  batchBytes = 0,
  // Pooled ACCOUNT storage (the tier entitlement) — the only ceiling.
  // poolUsedBytes is the account-wide storage already in use ACROSS ALL
  // PROJECTS and across all artifact kinds (photos, source video, renders), so
  // it already includes this project's existingBytes — only the batch is added
  // on top. poolTotalBytes null = allowance unknown (e.g. entitlements still
  // loading); the pooled meter is then omitted rather than shown as a 0-byte pool.
  poolTotalBytes = null,
  poolUsedBytes = 0,
  // The account-wide usage figure is async. While it's loading, poolUsedBytes
  // is still 0 (the hook's initial state) even for an account that's near its
  // ceiling — so we must NOT emit an over-pool verdict off that stale 0, and we
  // must NOT let the batch pass the pooled check just because usage is unknown
  // (the fail-open window #362 flagged). poolLoading=true defers the verdict:
  // the pooled dimension is treated as "calculating", blocked until it resolves.
  poolLoading = false,
}) {
  const projectedPhotos = existingPhotos + batchPhotos;
  const projectedBytes = existingBytes + batchBytes;

  const hasPool = Number.isFinite(poolTotalBytes) && poolTotalBytes > 0;
  // Verdict is pending only when we know there IS a pool but not yet how full
  // it is. Once usage is known, poolPending is false and overPoolCap is a real
  // number comparison; while pending we don't claim over/under — we withhold.
  const poolPending = hasPool && poolLoading;
  const projectedPoolBytes = poolUsedBytes + batchBytes;
  const overPoolCap = hasPool && !poolPending && projectedPoolBytes > poolTotalBytes;

  return {
    projectedPhotos,
    projectedBytes,
    // Pooled account-storage dimension — the one ceiling.
    hasPool,
    poolPending,
    poolTotalBytes: hasPool ? poolTotalBytes : null,
    poolUsedBytes,
    projectedPoolBytes,
    overPoolCap,
    poolBytesRemaining: hasPool ? Math.max(0, poolTotalBytes - poolUsedBytes) : null,
    // Fail CLOSED while the pooled usage is still resolving: a batch can't slip
    // through the 0-used window, and it can't over-fill a pool we can't yet see.
    blocked: overPoolCap || poolPending,
  };
}

export function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export function formatDuration(seconds) {
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  if (seconds < 60) return `about ${Math.max(1, Math.round(seconds))}s`;
  if (seconds < 3600) {
    const m = Math.round(seconds / 60);
    return `about ${m} min`;
  }
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return m > 0 ? `about ${h}h ${m}m` : `about ${h}h`;
}
